import { useState, useMemo, useEffect } from 'react';
import { autorun } from 'mobx';
import {
    ColumnDefinitionEnumType,
    QueueClassEnumType,
    QueueColumnFilter,
    UnitQueueWireDto,
} from '@mark43/cad-api';
import { FilterDirective, GroupingDirective } from '@list-store';
import { useEventNotificationsStore } from '../event-notifications/use-event-notifications';
import { UnitQueueRowData } from '../socket/stream-types';
import { workspacePanelIsQueue } from '../panels/workspace-queue-exists';
import { UnitQueueViewsListener, useUnitQueueViews } from './unit-queue-views-provider';
import { UnitQueueStore } from './store/unit-queue-store';
import { useRegisterQueueStore } from '../queues/queue-registry/use-register-queue-store';
import { useUnitTimersStore } from '../unit-timers/use-unit-timers-store';
import { useWorkspacePanel } from '../panels/hooks/use-workspace-panel';
import {
    filterQueueViewByQueueColumnFilters,
    filterQueueViewsByAssignmentStatus,
    filterQueueViewsByClass,
    filterQueueViewsBySearchValue,
    filterUnitQueueViewByDispatchArea,
    getQueueColumnFiltersByType,
} from '../queues/queue-filter-helpers';
import { useApplicationType } from '../common/hooks/use-application-type';
import { useDispatchAreaStore } from '../dispatch-areas/use-dispatch-areas';
import { useSyncWorkspacePanelToQueueStore } from '../queues/use-sync-workspace-panel-to-queue-store';
import { useFrUnitIdsToShowAndUnitsGroupingData } from './use-fr-unit-ids-to-show-and-units-grouping-data';
import { REGULAR_ASCENDING_SORT_DIRECTION } from './constants';
import { getGroupComparator } from './utils';

function createFilterDirective(props: {
    filterQuery: string;
    dispatchAreaIds: number[];
    queueColumnFilters: QueueColumnFilter[];
    isPinToPrimaryActive: boolean;
    queueClasses: QueueClassEnumType[];
    assignmentStatus: undefined | boolean;
    unitIdsToAlwaysShow?: number[];
    isFirstResponder: boolean;
}): FilterDirective<UnitQueueWireDto> {
    const {
        filterQuery,
        dispatchAreaIds,
        queueColumnFilters,
        isPinToPrimaryActive,
        queueClasses,
        assignmentStatus,
        unitIdsToAlwaysShow,
    } = props;
    const queueColumnFiltersByType = getQueueColumnFiltersByType(queueColumnFilters);
    return (item: UnitQueueWireDto) => {
        const searchValueToUse = isPinToPrimaryActive
            ? item.searchValue
            : item.searchValueIndividualUnit;
        const filtersToUse = isPinToPrimaryActive
            ? item.groupedQueueFiltersMetadata
            : item.queueFiltersMetadata;
        if (unitIdsToAlwaysShow?.includes(item.id)) return true;
        if (!filterQueueViewsBySearchValue(searchValueToUse, filterQuery)) return false;
        if (!filterQueueViewsByAssignmentStatus(item, assignmentStatus)) return false;
        if (!filterQueueViewsByClass(item, queueClasses)) return false;
        if (
            !props.isFirstResponder &&
            !filterUnitQueueViewByDispatchArea(item, dispatchAreaIds, queueColumnFiltersByType)
        ) {
            return false;
        }
        if (!filterQueueViewByQueueColumnFilters(filtersToUse, queueColumnFiltersByType)) {
            return false;
        }
        return true;
    };
}

/**
 * This should never be imported directly outside of unit-queue-state-provider
 */
export function useIntegratedUnitQueueStore() {
    const workspacePanel = useWorkspacePanel();
    workspacePanelIsQueue(workspacePanel);
    const { addListener, removeListener, unitQueueViewsRef } = useUnitQueueViews();
    const unitTimersStore = useUnitTimersStore();
    const eventNotificationsStore = useEventNotificationsStore();
    const dispatchAreaStore = useDispatchAreaStore();
    const { isFirstResponder } = useApplicationType();
    const { unitIdsToAlwaysShow, getGroupValueData } = useFrUnitIdsToShowAndUnitsGroupingData();

    const store = useState(
        () =>
            new UnitQueueStore({
                workspacePanel,
                eventNotificationsStore,
                unitTimersStore,
                manualFilterDirective: true,
            })
    )[0];

    useSyncWorkspacePanelToQueueStore(workspacePanel, store);

    useRegisterQueueStore(store);

    useEffect(() => {
        return autorun(() => {
            store.listStore.setFilterDirective(
                createFilterDirective({
                    isPinToPrimaryActive: !!store.workspacePanel.queue.pinBackupsToPrimaryUnits,
                    filterQuery: store.filterQuery,
                    dispatchAreaIds: dispatchAreaStore.monitoredAndOwnedDispatchAreaIds,
                    queueColumnFilters: workspacePanel.queue.unitQueueColumnFilters,
                    queueClasses: workspacePanel.queue.queueClasses,
                    assignmentStatus: workspacePanel.queue.assignmentStatus,
                    unitIdsToAlwaysShow,
                    isFirstResponder,
                }) as FilterDirective<UnitQueueRowData>
            );
        });
    }, [unitIdsToAlwaysShow, store, workspacePanel, isFirstResponder, dispatchAreaStore]);

    const columnDefinition = store.workspacePanel.queue.groupByQueueColumnDefinition;

    const getGroupValue = getGroupValueData(columnDefinition as ColumnDefinitionEnumType);

    const comparator = getGroupComparator(
        REGULAR_ASCENDING_SORT_DIRECTION,
        columnDefinition as ColumnDefinitionEnumType
    );

    const frUnitsGrouping = useMemo(
        () =>
            ({
                getGroupValue,
                comparator,
            } as GroupingDirective<UnitQueueRowData>),
        [comparator, getGroupValue]
    );

    useEffect(() => {
        if (isFirstResponder) {
            return autorun(() => {
                store.listStore.setGroupingDirective(frUnitsGrouping);
                store.workspacePanel.queue.setGroupBySortDirection(
                    REGULAR_ASCENDING_SORT_DIRECTION
                );
            });
        }
        // it runs only to set the initial groupingDirective and when there are changes in the assignedUnits
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFirstResponder, unitIdsToAlwaysShow]);

    const listener: UnitQueueViewsListener = useMemo(() => {
        return {
            onBootstrap() {
                store.setRows(Object.values(unitQueueViewsRef.current));
            },
            onUpdate(bundle) {
                if (!workspacePanel.queue.pinBackupsToPrimaryUnits) {
                    store.upsertRows(bundle.unitQueueViews);
                } else {
                    if (workspacePanel.queue.pinBackupsToPrimaryUnits) {
                        store.upsertRows(bundle.unitQueueViews, true);
                    }
                }
                if (bundle.offDutyUnitIds) {
                    store.removeRows(bundle.offDutyUnitIds);
                }
            },
        };
    }, [store, unitQueueViewsRef, workspacePanel.queue.pinBackupsToPrimaryUnits]);

    useEffect(() => {
        addListener?.(listener);
        return () => {
            removeListener?.(listener);
        };
    }, [addListener, unitQueueViewsRef, listener, removeListener, store]);

    return { store };
}
